import React, { FunctionComponent } from 'react';
import { range } from 'lodash';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';
import { DAY_LIST } from '../../../utils/constants';
import { Toggle } from '../../Input/Toggle/Toggle';
import { Select } from '../../Input/Select/Select';
import { addDefaultLending } from '../defaults';

const title = 'What hours can we reach you?';
const subTitle =
  'Your business hours help us know the best time to call you for any questions';
const cardTitle = 'Business Hours';
const cardDescription =
  'Enter the sales hours your dealership is open to customers.';

const HOURS_OPTIONS = [
  ...[12, ...range(1, 12)].map((i) => `${i}:00 AM`),
  ...[12, ...range(1, 12)].map((i) => `${i}:00 PM`),
].map((label, index) => ({ label, value: index }));

export const DealerLaunchLendingFinanceHours: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({
    defaultValues: addDefaultLending(dealer),
  });
  const dayMap = DAY_LIST.reduce((acc: any, day: string) => {
    // eslint-disable-next-line no-param-reassign
    acc[day] = form.watch(`lending.businessHours.${day}.enabled`);
    return acc;
  }, {});

  const onSubmit = async (data: Dealer) => {
    // validate start time must be be before end time
    let hasTimeError = false;

    DAY_LIST.forEach((day) => {
      if (
        data.lending?.businessHours &&
        parseInt(`${data.lending?.businessHours?.[day].start || 0}`, 10) >=
          parseInt(`${data.lending?.businessHours?.[day].end || 0}`, 10) &&
        data.lending?.businessHours?.[day].enabled
      ) {
        form.setError(`lending.businessHours.${day}.start`, {
          message: 'Start time must be before end time',
        });
        hasTimeError = true;
      }
    });

    if (hasTimeError) {
      return;
    }

    const dealerUpdate: Dealer = {
      ...data,
      lending: {
        ...dealer.lending,
        ...data.lending,
      },
    };

    // force start and end times into number
    DAY_LIST.forEach((day) => {
      const dayObject = dealerUpdate.lending?.businessHours;
      if (dayObject) {
        dayObject[day].start = parseInt(`${dayObject[day].start}`, 10);
        dayObject[day].end = parseInt(`${dayObject[day].end}`, 10);
      }
    });

    await nextStep(dealerUpdate);
  };

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title={title}
      subTitle={subTitle}
      cardTitle={cardTitle}
      cardSubTitle={cardDescription}
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <div className={styles.stepColumn}>
        {DAY_LIST.map((day) => (
          <div className={styles.timeInputRow} key={day}>
            <Toggle
              className={styles.switchContainer}
              label={day}
              name={`lending.businessHours.${day}.enabled` as const}
              variant="default"
              form={form}
            />
            <span className={styles.openClosedContainer}>
              {dayMap[day] ? 'Open' : 'Closed'}
            </span>
            {dayMap[day] && (
              <div className={styles.stepRow}>
                <Select
                  label=""
                  name={`lending.businessHours.${day}.start` as const}
                  width="sm"
                  options={HOURS_OPTIONS}
                  form={form}
                  validation={{ required: true }}
                />
                <div className={styles.hoursSeparator} />
                <Select
                  label=""
                  name={`lending.businessHours.${day}.end` as const}
                  width="sm"
                  options={HOURS_OPTIONS}
                  form={form}
                  validation={{ required: true }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </DealerLaunchFormCardLayout>
  );
};
